import { useParams } from "react-router-dom";
import { CalendarDays, MapPin, Navigation, Ticket } from "lucide-react";
import { formatEventDate, formatTime, isPast, useApi } from "@/lib/api";
import { RichContent } from "@/components/site/RichContent";
import { ShareMenu } from "@/components/site/ShareMenu";
import { BackLink, DetailSkeleton, NotFound } from "@/components/site/DetailBits";
import { directionsUrl } from "@/components/site/PlacesMap";
import { AddToCalendar } from "@/components/site/AddToCalendar";
import { EventPoster } from "@/components/site/EventPoster";

const Info = ({ icon: Icon, children, testId }) => (
  <p className="flex items-start gap-3 text-white text-base md:text-lg" data-testid={testId}>
    <Icon className="w-5 h-5 text-primary mt-1 shrink-0" /> <span>{children}</span>
  </p>
);

export default function EventDetail() {
  const { slug } = useParams();
  const { data: e, isLoading, isError } = useApi(`/events/${slug}`);
  if (isLoading) return <DetailSkeleton />;
  if (isError || !e) return <NotFound to="/agenda" label="Retour à l'agenda" />;
  const past = isPast(e.date);

  return (
    <article className="pt-32 pb-32" data-testid="event-detail">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <BackLink to="/agenda" label="Retour à l'agenda" />
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 mt-10">
          <div className="lg:sticky lg:top-28 self-start">
            <div className="overflow-hidden bg-[hsl(var(--surface))]">
              <img src={e.image || "/brand/fallback.jpg"} alt={e.title} className="w-full h-auto object-cover reveal" />
            </div>
          </div>
          <div className="reveal" style={{ animationDelay: "120ms" }}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary">{past ? "Événement passé" : "Prochainement"}</p>
            <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white mt-3 break-words" data-testid="event-detail-title">{e.title}</h1>
            <div className="space-y-3 mt-8 border-y border-white/10 py-8">
              <Info icon={CalendarDays} testId="event-detail-date">{formatEventDate(e.date)} · {formatTime(e.date)}</Info>
              {e.venue && <Info icon={MapPin} testId="event-detail-venue"><strong>{e.venue}</strong>{e.address ? ` — ${e.address}` : ""}</Info>}
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              {e.ticket_url && !past && (
                <a href={e.ticket_url} target="_blank" rel="noreferrer" data-testid="event-detail-ticket-btn"
                  className="inline-flex items-center gap-3 bg-primary text-white font-display uppercase text-lg px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">
                  <Ticket className="w-5 h-5" /> Billetterie
                </a>
              )}
              {!past && (
                <AddToCalendar event={e} className="border-2 border-white text-white font-display uppercase text-lg px-8 py-4 hover:bg-white hover:text-black" />
              )}
              {(e.venue || e.city) && (
                <a href={directionsUrl(e)} target="_blank" rel="noreferrer" data-testid="event-detail-go-btn"
                  className="inline-flex items-center gap-3 border-2 border-white text-white font-display uppercase text-lg px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">
                  <Navigation className="w-5 h-5" /> Y aller
                </a>
              )}
              <ShareMenu text={`${e.title}${e.venue ? ` — ${e.venue}` : ""}, ${formatEventDate(e.date)}`} testId="event-share" label="Partager"
                className="border-2 border-white text-white font-display uppercase text-lg px-8 py-4 hover:bg-white hover:text-black" />
              <EventPoster event={e} className="border-2 border-white text-white font-display uppercase text-lg px-8 py-4 hover:bg-white hover:text-black" />
            </div>
            <div className="mt-12">
              <RichContent content={e.content} fallback={e.excerpt} embeds={e.embeds} />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
